import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { loadOrders } from '@features/orders/actions/load-orders.actions';
import * as fromOrders from '@features/orders/reducers/orders.reducer';
import { OrderFilters } from '@shared/models/order-filters';
import { PaginationState } from '@shared/models/pagination-state';
import { Sorting } from '@shared/models/sorting';
import { Observable } from 'rxjs';
import { LoadingState } from '@shared/types/loading-state';
import { Order } from '@shared/models/order';
import { downloadOrdersCsv } from '../actions/download-orders-csv.actions';
import { emailOrdersExport } from '../actions/email-orders-export.actions';
import { loadOrder } from '../actions/load-order.actions';
import { setSelectedOrderId } from '../actions/set-selected-order-id.actions';
import { cancelOrder } from '../shared/actions/cancel-order.actions';
import { suspendOrder } from '../shared/actions/suspend-order.actions';
import { unsuspendOrder } from '../shared/actions/unsuspend-order.actions';
import { setOrderFilters } from '../actions/set-order-filters.actions';
import { sendPaymentLink } from '../actions/send-payment-link.actions';
import { UserType } from '@shared/types/user-type';
import { HistoryItem } from '@shared/models/history-item';
import { EsetSubscription } from '@shared/models/eset-subscription';
import { OrderItem } from '@shared/models/order-item';
import { Offering } from '@shared/models/offering';
import { OrderItemsService } from './order-items.service';
import { RenewalsMetrics } from '@shared/models/renewals-metrics';
import {
  downloadRenewalsCsv,
  updateRenewalsCsvProgress,
} from '@features/renewals/actions/download-renewals-csv.action';

@Injectable({
  providedIn: 'root',
})
export class OrdersService {
  constructor(
    private store: Store,
    private orderItemsService: OrderItemsService,
  ) {}

  loadOrders(filters: OrderFilters, paginationState: PaginationState, sorting: Sorting) {
    this.store.dispatch(loadOrders({ filters, paginationState, sorting }));
  }

  loadOrder(orderId: number) {
    this.store.dispatch(loadOrder({ orderId, requiresReload: false }));
  }

  cancelOrder(orderId: number, reason: string, refundPayment?: boolean, forUnprocessedQuote = false, quoteId?: number) {
    this.store.dispatch(
      cancelOrder({
        orderId: orderId,
        reason: reason,
        forUnprocessedQuote: forUnprocessedQuote,
        quoteId: quoteId,
        refundPayment: refundPayment,
      }),
    );
  }

  suspendOrder(orderId: number, reason: string) {
    this.store.dispatch(suspendOrder({ orderId: orderId, reason: reason }));
  }

  unsuspendOrder(orderId: number, reason: string) {
    this.store.dispatch(unsuspendOrder({ orderId: orderId, reason: reason }));
  }

  setOrderFilters(filters: OrderFilters | undefined) {
    this.store.dispatch(setOrderFilters({ filters }));
  }

  downloadOrdersCsv() {
    this.store.dispatch(downloadOrdersCsv());
  }

  emailOrdersExport() {
    this.store.dispatch(emailOrdersExport());
  }

  downloadRenewalsCsv(filters: OrderFilters) {
    this.store.dispatch(downloadRenewalsCsv({ filters }));
  }

  sendPaymentLink(orderId: number) {
    this.store.dispatch(sendPaymentLink({ orderId }));
  }

  getOrders(): Observable<Order[]> {
    return this.store.select(fromOrders.selectAllOrders);
  }

  getOrderDetailsById(orderId: number): Observable<Order | undefined> {
    return this.store.select(fromOrders.selectOrderById(orderId));
  }

  getOrderHistorySortedByDate(): Observable<HistoryItem[] | undefined> {
    return this.store.select(fromOrders.orderHistorySortedByDate);
  }

  getOrdersLoadingState(): Observable<LoadingState> {
    return this.store.select(fromOrders.selectOrdersLoadingState);
  }

  getOrdersLoadingText(): Observable<string | undefined> {
    return this.store.select(fromOrders.selectOrdersLoadingText);
  }

  getOrdersPaginationState(): Observable<PaginationState> {
    return this.store.select(fromOrders.selectOrdersPaginationState);
  }

  getOrderFilters(): Observable<OrderFilters | undefined> {
    return this.store.select(fromOrders.selectOrderFilters);
  }

  setSelectedOrderId(orderId: number): void {
    this.store.dispatch(setSelectedOrderId({ orderId }));
  }

  getSelectedOrder(): Observable<Order | undefined> {
    return this.store.select(fromOrders.selectSelectedOrder);
  }

  getSelectedOrderItemsPricing(): Observable<{ order_item: OrderItem; offering: Offering | undefined }[] | undefined> {
    return this.store.select(fromOrders.selectSelectedOrderItemsPricing);
  }

  getCanCancelSelectedOrder(): Observable<boolean> {
    return this.store.select(fromOrders.selectCanCancelSelectedOrder);
  }

  getSelectedOrderHasActiveEsetServices(): Observable<boolean> {
    return this.store.select(fromOrders.selectSelectedOrderHasActiveEsetServices);
  }

  getSuspendOrUnsuspendSelectedOrder(): Observable<string> {
    return this.store.select(fromOrders.selectSuspendOrUnsuspendSelectedOrder);
  }

  getOrderStatusToDisplay(order: Order): string | undefined {
    const hasActiveLicenses = order.attributes.order_items?.some(
      (order_item) =>
        (order_item.attributes.itemable as EsetSubscription)?.attributes?.subscription_status === 'active',
    );
    if (['draft', 'suspended', 'cancelled', 'inactive'].includes(order.attributes.order_status)) {
      return order.attributes.order_status;
    } else if (order.attributes.order_status == 'processed') {
      return hasActiveLicenses || ['isp', 'msp'].includes(order.attributes.order_type) ? 'complete' : 'inactive';
    }
    return 'inactive';
  }

  getRenewalMetrics(): Observable<RenewalsMetrics | undefined> {
    return this.store.select(fromOrders.selectRenewalsMetrics);
  }

  canViewInvoicePdf(userType: UserType, tradingEntityGroupId: string, order: Order): boolean {
    return (
      !!order.attributes.invoice_id &&
      (userType == 'intranet' ||
        (!['global_e_store', 'online_store', 'auto_renewal_system'].includes(order.attributes.order_source) &&
          tradingEntityGroupId === order.attributes.billing_group_id))
    );
  }

  hasExpiredItems(order: Order): boolean {
    return order.attributes.order_items?.some((orderItem) => this.orderItemsService.isExpired(orderItem));
  }

  hasExpiringItems(order: Order): boolean {
    return order?.attributes.order_items?.some((item) => this.orderItemsService.isExpiring(item));
  }

  isRenewable(order: Order): boolean {
    return order.attributes.order_items?.some((orderItem) => this.orderItemsService.isRenewable(orderItem));
  }

  canBeExtended(order: Order): boolean {
    return order.attributes.order_items?.some((orderItem) => this.orderItemsService.canBeExtended(orderItem));
  }

  canBeConverted(order: Order): boolean {
    return order.attributes.order_items?.some((orderItem) => this.orderItemsService.canBeConverted(orderItem));
  }

  updateRenewalsCsvProgress(paginationState: PaginationState): void {
    this.store.dispatch(updateRenewalsCsvProgress({ paginationState }));
  }
}
